// MST
import { types, cast, flow, detach } from 'mobx-state-tree'

// Models
import { RoleListRef } from 'models/back-office/master/role/role.store'

// Extensions
import {
  withHelper_baseResponseDropdownTypesModel,
  withHelper_fileDownload,
  withHelper_paginationProps,
} from 'models/extensions/with-helper'
import { withEnvironment } from 'models/extensions/with-environment'

// Interfaces
import {
  IUserApiDetailResponse,
  IUserApiDownloadResponse,
  IUserApiDropdownResponse,
  IUserApiListResponse,
  IUserApiNullResponse,
  IUserApiValidateEmailResponse,
} from 'interfaces/back-office/user/user-api.interface'
import {
  IUserApproveAttrs,
  IUserCreateAttrs,
  IUserDeleteAttrs,
  IUserDownloadAttrs,
  IUserGetListAttrs,
  IUserGetDetailAttrs,
  IUserGetDropdownAttrs,
  IUserRejectAttrs,
  IUserUpdateAttrs,
  IUserValidateEmailAttrs,
} from 'interfaces/back-office/user/user-view.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Lodash
import pick from 'lodash/pick'

// Utils
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Constant
import { LOADING, GENDERS } from 'constant/shared.constant'

// Extensions
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Types
const { maybeNull, number, string, boolean, array, optional, model, enumeration } = types

// Loading
const { BASE, DETAIL, ACTION, DROPDOWN } = LOADING

// User Props
const userProps = {
  id: maybeNull(number),
  number: maybeNull(number),
  name: maybeNull(string),
  email: maybeNull(string),
  npk: maybeNull(string),
  nik: maybeNull(string),
  companyName: maybeNull(string),
  companyId: maybeNull(number),
  branchName: maybeNull(string),
  branchId: maybeNull(number),
  fieldName: maybeNull(string),
  fieldId: maybeNull(number),
  idCardPhotoBlobId: maybeNull(string),
  bachelorCertificatePhotoBlobId: maybeNull(string),
  familyCardPhotoBlobId: maybeNull(string),
  workCardPhotoBlobId: maybeNull(string),
  status: maybeNull(string),
  roles: optional(array(RoleListRef), []),
  accountStatus: maybeNull(boolean),
  profilePhoto: maybeNull(string),
  phoneNumber: maybeNull(string),
  address: maybeNull(string),
  gender: maybeNull(enumeration('Gender', Object.values(GENDERS))),
  birthDate: maybeNull(string),
  birthPlace: maybeNull(string),
  nationality: maybeNull(string),
  postalCode: maybeNull(string),
  phone: maybeNull(string),
  lastEducation: maybeNull(string),
  institutionName: maybeNull(string),
  idCardPhoto: maybeNull(string),
  pasPhoto: maybeNull(string),
  workPhone: maybeNull(string),
  isPhoneNumberValidated: maybeNull(boolean),
  workCardPhoto: maybeNull(string),
  supervisorName: maybeNull(string),
  supervisorEmail: maybeNull(string),
  jobTitle: maybeNull(string),
  companyAddress: maybeNull(string),
  companyPostalCode: maybeNull(string),
  companyEmail: maybeNull(string),
  companyFax: maybeNull(string),
  companyPhone: maybeNull(string),
}

// List Of Users
const UserList = optional(
  array(model('UserList').props(pick(userProps, ['id', 'number', 'name', 'companyName', 'fieldName', 'status', 'accountStatus']))),
  [],
)

// List of Users that paginated
const UserPaginated = model('UserPaginated').props({ ...withHelper_paginationProps, list: UserList })

// User reject form
const UserRejectForm = model('UserRejectForm').props({ reason: optional(string, '') })

// User approve form
const UserApproveForm = model('UserApproveForm').props({ roleIds: optional(array(number), []) })

// Detail of user
const UserDetail = model('UserDetail').props(userProps)

// Base Model
const UserStoreModel = types
  .model('User')
  .props({
    user_loading: withHelper_commonLoadingModel,
    user_list: optional(UserPaginated, {}),
    user_detail: optional(UserDetail, {}),
    user_rejectForm: optional(UserRejectForm, {}),
    user_approveForm: optional(UserApproveForm, {}),
    user_dropdown: withHelper_baseResponseDropdownTypesModel('UserDropdown'),
  })
  .actions(self => ({
    user_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.user_loading, payload)

      detach(self.user_loading)
      self.user_loading = cast(map)
    },
    user_SET_LIST: (payload: IUserApiListResponse['res']['data']) => {
      self.user_list = cast({
        ...payload,
        list: payload.list.map((list, index) => ({
          ...list,
          number: commonUtils_renderTableNumber(index, payload),
        })),
      })
    },
    user_SET_DETAIL: (payload: IUserApiDetailResponse['res']['data']): void => {
      self.user_detail = cast(payload)
    },
    user_SET_REJECT_FORM: (payload: { reason: string }): void => {
      self.user_rejectForm = cast(payload)
    },
    user_SET_APPROVE_FORM: (payload: { roleIds: number[] }): void => {
      self.user_approveForm = cast(payload)
    },
    user_SET_DROPDOWN: (payload: IUserApiDropdownResponse['res']['data']): void => {
      self.user_dropdown = cast(payload.filter(result => result.value))
    },
    user_CLEAR_APPROVE_FORM: (): void => {
      self.user_approveForm = cast({})
    },
    user_CLEAR_REJECT_FORM: (): void => {
      self.user_rejectForm = cast({})
    },
    user_CLEAR_LIST: (): void => {
      self.user_list = cast({})
    },
    user_CLEAR_DETAIL: (): void => {
      self.user_detail = cast({})
    },
    user_CLEAR_DROPDOWN: (): void => {
      self.user_dropdown = cast([])
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch users
     *
     * @param {IUserGetListAttrs} payload
     *
     * @return {any} any
     */
    user_fetchList: flow(function* (payload: IUserGetListAttrs) {
      self.user_SET_LOADING({ type: BASE, value: true })

      const response: IUserApiListResponse = yield self.environment.userApi.user_getList({
        body: payload.body,
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.user_SET_LIST(response.res.data)

        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch user detail
     *
     * @param {IUserGetDetailAttrs} payload
     *
     * @return {any} any
     */
    user_fetchDetail: flow(function* (payload: IUserGetDetailAttrs) {
      self.user_SET_LOADING({ type: DETAIL, value: true })

      const response: IUserApiDetailResponse = yield self.environment.userApi.user_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        self.user_SET_DETAIL(response.res.data)

        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Create users
     *
     * @param {IUserCreateAttrs} payload
     *
     * @return {any} any
     */
    user_create: flow(function* (payload: IUserCreateAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiNullResponse = yield self.environment.userApi.user_create({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Approve user
     *
     * @param {IUserApproveAttrs} payload
     *
     * @return {any}
     */
    user_approve: flow(function* (payload: IUserApproveAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiNullResponse = yield self.environment.userApi.user_approve({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Reject user
     *
     * @param {IUserRejectAttrs} payload
     *
     * @return {any}
     */
    user_reject: flow(function* (payload: IUserRejectAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiNullResponse = yield self.environment.userApi.user_reject({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Delete user
     *
     * @param {IUserDeleteAttrs} payload
     *
     * @return {any}
     */
    user_delete: flow(function* (payload: IUserDeleteAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiNullResponse = yield self.environment.userApi.user_delete({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Update user
     *
     * @param {IUserUpdateAttrs} payload
     *
     * @return {any}
     */
    user_update: flow(function* (payload: IUserUpdateAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiNullResponse = yield self.environment.userApi.user_update({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // If user want to to do next when response is error
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Validate user email
     *
     * @param {IUserValidateEmailAttrs} payload
     *
     * @return {any}
     */
    user_validateEmail: flow(function* (payload: IUserValidateEmailAttrs) {
      self.user_SET_LOADING({ type: ACTION, value: true })

      const response: IUserApiValidateEmailResponse = yield self.environment.userApi.user_validateEmail({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess(response.res.data)
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError({ message: response.res.message || '', isError: true })
      }

      self.user_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Get dropdown of user
     *
     * @param {IUserGetDropdownAttrs} payload
     *
     * @return {any}
     */
    user_fetchDropdown: flow(function* (payload: IUserGetDropdownAttrs) {
      self.user_SET_LOADING({ type: DROPDOWN, value: true })

      const response: IUserApiDropdownResponse = yield self.environment.userApi.user_getDropdown({
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.user_SET_DROPDOWN(response.res.data)

        // If user want to do next when response is ok
        if (payload?.onSuccess) payload.onSuccess(response.res.data)
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError()
      }

      self.user_SET_LOADING({ type: DROPDOWN, value: false })
    }),

    /**
     * @description Download
     *
     * @param {IUserDownloadAttrs} payload
     *
     * @return {any} any
     */
    user_download: flow(function* (payload: IUserDownloadAttrs) {
      const response: IUserApiDownloadResponse = yield self.environment.userApi.user_download({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })

      if (response.ok) {
        withHelper_fileDownload(response.res, response.headers!)

        // Do when response is success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response is failed
        if (payload?.onError) payload.onError()
      }
    }),
  }))

export const createUserStoreDefaultModel = () => optional(UserStoreModel, {})
